"use client"

import { createContext, useContext, type ComponentProps, type MouseEvent, type ReactNode } from "react"

import { ExternalLink } from "@/components/ui/external-link"
import { opensElsewhere } from "@/lib/helpers/link-click"
import { useShallowQueryParams } from "@/lib/hooks/use-navigation"
import { buildRoute } from "@/lib/routes"
import { retrieveUTMParams } from "@/lib/services/utm"
import { trackSignupEntryClick } from "@/platform/observability/analytics/v2/signup"
import { useClientSessionId } from "@/platform/session/auth/use-client-session-id"

const InlineSignupContext = createContext(false)

export const InlineSignupProvider = ({ enabled, children }: { enabled: boolean; children: ReactNode }) => <InlineSignupContext value={enabled}>{children}</InlineSignupContext>

export const useInlineSignup = (): boolean => useContext(InlineSignupContext)

const useSignupEntry = (location: string) => {
  const inline = useInlineSignup()
  const { sessionId, isMounted } = useClientSessionId()
  const updateQuery = useShallowQueryParams()

  const href = inline ? "?signup=1" : buildRoute.externalSignup(sessionId, isMounted ? retrieveUTMParams() : undefined)
  const rel = inline ? "nofollow" : undefined

  const onClick = (event: MouseEvent<HTMLElement>) => {
    trackSignupEntryClick({ location, arm: inline ? "inline" : "marketing" })
    if (!inline || event.defaultPrevented || opensElsewhere(event)) return
    event.preventDefault()
    updateQuery({ signup: 1, productId: null, variantId: null })
  }

  return { inline, href, rel, onClick }
}

interface SignupLinkProps extends Omit<ComponentProps<"a">, "href"> {
  location: string
}

export const SignupLink = ({ location, children, onClick: onClickProp, ...props }: SignupLinkProps) => {
  const { inline, href, rel, onClick } = useSignupEntry(location)
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClickProp?.(event)
    onClick(event)
  }

  if (inline) {
    return (
      <a {...props} href={href} rel={[props.rel, rel].filter(Boolean).join(" ")} onClick={handleClick}>
        {children}
      </a>
    )
  }

  return (
    <ExternalLink {...props} href={href} onClick={handleClick}>
      {children}
    </ExternalLink>
  )
}
